import { ReactNode, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Crown, Lock, Sparkles } from 'lucide-react';

interface RelationshipIntelligenceGateProps {
  children: ReactNode;
}

const TRIAL_DAYS = 7;

function normalizePlan(raw: string | null): 'free' | 'basic' | 'premium' | 'pro' {
  const p = String(raw || 'free').toLowerCase();
  if (p === 'basic' || p === 'premium' || p === 'pro') return p;
  return 'free';
}

function trialStorageKey(userId: string | undefined): string {
  return userId ? `tension_ri_trial_started_at:${userId}` : 'tension_ri_trial_started_at';
}

export function RelationshipIntelligenceGate({ children }: RelationshipIntelligenceGateProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const gate = useMemo(() => {
    const plan = normalizePlan(localStorage.getItem('tension_subscription'));
    if (plan === 'pro') {
      return { allow: true as const, trialBanner: null as null | string };
    }

    const key = trialStorageKey(user?.id);
    const now = Date.now();
    const existing = localStorage.getItem(key);
    const startedAt = existing ? Number(existing) : now;

    if (!existing) {
      localStorage.setItem(key, String(startedAt));
    }

    const trialMs = TRIAL_DAYS * 24 * 60 * 60 * 1000;
    const expiresAt = startedAt + trialMs;
    const remainingMs = expiresAt - now;

    if (remainingMs > 0) {
      const daysLeft = Math.max(1, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));
      return {
        allow: true as const,
        trialBanner: `Trial active: ${daysLeft} day${daysLeft === 1 ? '' : 's'} left. Upgrade to Pro to keep full access.`,
      };
    }

    return { allow: false as const, trialBanner: null as null | string };
  }, [user?.id]);

  if (!gate.allow) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-10 bg-gradient-to-b from-white via-violet-50/30 to-neutral-50">
        <Card className="max-w-xl w-full p-7 border-violet-200 bg-white/95 shadow-md space-y-4 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
            <Lock className="w-6 h-6 text-violet-700" />
          </div>
          <h1 className="text-2xl font-semibold text-neutral-900">Relationship Intelligence is Pro-only</h1>
          <p className="text-sm text-neutral-600">
            Your 7-day trial has ended. Upgrade to Pro to keep access to the full Relationship Intelligence Suite.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button onClick={() => navigate('/pricing')} className="gap-2">
              <Crown className="w-4 h-4" />
              Upgrade to Pro
            </Button>
            <Link to="/home">
              <Button variant="outline">Back to Home</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      {gate.trialBanner && (
        <div className="max-w-4xl mx-auto px-6 pt-6">
          <Card className="p-4 border-amber-200 bg-gradient-to-r from-amber-50 to-violet-50 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-amber-900">{gate.trialBanner}</p>
              <Link to="/pricing">
                <Button size="sm" variant="outline" className="gap-1.5 border-amber-300 text-amber-900">
                  <Sparkles className="w-4 h-4" />
                  Upgrade
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      )}
      {children}
    </>
  );
}
